/**
 * A quote before payment („ფასის შეთავაზება", 1.58; spec §3) — the priced list
 * a builder takes to his boss or pays by bank transfer. It moves no money and
 * no stock; the till prints it on A4 through the commercial invoice's paper
 * (lib/invoice.ts, kind 'quote') and may keep it with a held cart.
 *
 * Every row adds up (Codex's design review #9: „13 × 1.20 = 12.80" must never
 * print): a line with boxes is a box row and a piece row; a group, wholesale or
 * price-promotion line prints the unit price it is sold at; a line of a
 * several-product bundle prints at retail and the bundle's saving is one row of
 * its own. Σ rows = the cart's lines; the cart discount is the paper's own row.
 */
import type { QuoteRow, QuoteSnapshot } from './mock-data'
import type { CartPriceLine, PricedLine } from './cart-pricing'
import type { Promotion } from './promotions'
import type { InvoiceModel, InvoiceShop } from './invoice'
import { vatInside } from './tax'
import { lariInWords } from './money-words'
import { roundMoney } from './money-round'

/** Documents are Georgian whatever the device speaks. */
const UNIT_WORD: Record<string, string> = { piece: 'ც', kg: 'კგ', m: 'მ', l: 'ლ' }

export type QuoteLineIn = CartPriceLine & { product_name: string; unit?: string | null }

export function quoteRows(lines: readonly QuoteLineIn[], priced: Map<string, PricedLine>, promotions: readonly Promotion[]): QuoteRow[] {
  const rows: QuoteRow[] = []
  const bundleSaving = new Map<string, { name: string; amount: number }>()
  for (const l of lines) {
    const p = priced.get(l.product_id)
    if (!p || l.quantity <= 0) continue
    const unit = UNIT_WORD[l.unit ?? 'piece'] ?? ''
    const promo = p.promo
    const promotion = promo ? promotions.find(x => x.id === promo.promotion_id) : undefined
    if (promo && promotion?.pack_product_id) {
      const pack = promotion.items[0]?.quantity ?? 0
      const boxes = pack > 0 ? Math.floor(l.quantity / pack + 1e-9) : 0
      const rest = roundMoney(l.quantity - boxes * pack)
      if (boxes > 0) rows.push({ name: `${l.product_name} — ყუთი (${pack} ც)`, qty: boxes, unit: 'ყუთი', price: promotion.bundle_price ?? 0, amount: roundMoney(boxes * (promotion.bundle_price ?? 0)) })
      if (rest > 0) rows.push({ name: l.product_name, qty: rest, unit, price: p.retail, amount: roundMoney(rest * p.retail) })
      continue
    }
    if (promo && promo.kind === 'price') {
      const each = roundMoney(p.amount / l.quantity)
      if (Math.abs(each * l.quantity - p.amount) < 0.005) {
        rows.push({ name: l.product_name, qty: l.quantity, unit, price: each, amount: roundMoney(p.amount) })
        continue
      }
    }
    if (promo) {
      // a several-product bundle (or a price promotion that is not a whole tetri a unit): retail, and the saving apart
      rows.push({ name: l.product_name, qty: l.quantity, unit, price: p.retail, amount: roundMoney(p.retail * l.quantity) })
      const b = bundleSaving.get(promo.promotion_id) ?? { name: promo.promotion_name, amount: 0 }
      b.amount += promo.promo_discount
      bundleSaving.set(promo.promotion_id, b)
      continue
    }
    rows.push({ name: l.product_name, qty: l.quantity, unit, price: p.unit_price, amount: roundMoney(p.amount) })
  }
  for (const b of bundleSaving.values()) {
    const amount = -roundMoney(b.amount)
    rows.push({ name: `აქცია: ${b.name}`, qty: 1, unit: '', price: amount, amount })
  }
  return rows
}

const tbilisi = (d: Date) => Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Tbilisi', year: '2-digit', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
}).formatToParts(d).map(p => [p.type, p.value]))

/** „211026-143205" — the date and the time to the second, Tbilisi (to the minute collided — Codex #9). */
export function quoteNumber(now: Date): string {
  const p = tbilisi(now)
  return `${p.day}${p.month}${p.year}-${p.hour === '24' ? '00' : p.hour}${p.minute}${p.second}`
}

/** The last day the quote holds: today in Tbilisi + `days`, as yyyy-mm-dd. */
export function validUntilFrom(now: Date, days: number): string {
  const p = tbilisi(now)
  const d = new Date(Date.UTC(2000 + Number(p.year), Number(p.month) - 1, Number(p.day) + days))
  return d.toISOString().slice(0, 10)
}

const dmy = (iso: string) => { const [y, m, d] = iso.slice(0, 10).split('-'); return `${d}.${m}.${y}` }

/** The quote as the invoice's paper draws it: the shop, the buyer, the rows, the total in words, the terms. */
export function quoteModel(q: QuoteSnapshot, shop: InvoiceShop, issuedAt: Date): InvoiceModel {
  const subtotal = roundMoney(q.rows.reduce((s, r) => s + r.amount, 0))
  const total = roundMoney(q.total)
  const p = tbilisi(issuedAt)
  return {
    kind: 'quote',
    number: q.number,
    date: `${p.day}.${p.month}.20${p.year}`,
    validUntil: dmy(q.valid_until),
    note: q.note,
    seller: { name: shop.companyName || '—', tin: shop.companyId || '—', address: shop.address || '', phone: shop.phone || '', bank: shop.bankDetails || '' },
    buyer: { name: q.buyer.name || '—', tin: q.buyer.tin || '—', address: q.buyer.address || '' },
    lines: q.rows.map(r => ({ name: r.name, qty: r.qty, unit: r.unit, price: r.price, amount: r.amount })),
    subtotal,
    discount: roundMoney(Math.max(0, subtotal - total)),
    bonus: 0,
    total,
    vat: shop.taxStatus === 'vat' ? roundMoney(vatInside(total)) : null,
    paid: null,
    due: null,
    words: lariInWords(total),
    rs: null,
  }
}
